import { Badge, Box, HStack, Status, Table, Text, VStack } from '@chakra-ui/react'
import type { MarketplaceDemand } from '../types'
import { isDemandOpen } from '../demandStatus'
import { MarketplaceStatusBadge } from './MarketplaceStatusBadge'

interface MarketplaceDemandTableProps {
  demands: MarketplaceDemand[]
  onOpenDemand: (demandId: string | number) => void
}

function formatPeriod(start?: string, end?: string) {
  if (!start && !end) return '—'

  const format = (value?: string) => {
    if (!value) return ''
    const [year, month, day] = value.split('-')
    return `${day}.${month}.${year}`
  }

  return `${format(start)}–${format(end)}`
}

function getDaysLeft(expiresAt?: string) {
  if (!expiresAt) return null

  const today = new Date()
  const expiry = new Date(expiresAt)

  today.setHours(0, 0, 0, 0)
  expiry.setHours(0, 0, 0, 0)

  return Math.ceil((expiry.getTime() - today.getTime()) / 86_400_000)
}

function DeadlineStatus({ expiresAt }: { expiresAt?: string }) {
  const daysLeft = getDaysLeft(expiresAt)

  if (daysLeft === null) return null

  if (daysLeft < 0) {
    return (
      <Status.Root size="sm" colorPalette="red">
        <Status.Indicator />
        Abgelaufen
      </Status.Root>
    )
  }

  if (daysLeft <= 4) {
    return (
      <Status.Root size="sm" colorPalette="yellow">
        <Status.Indicator />
        Noch {daysLeft} {daysLeft === 1 ? 'Tag' : 'Tage'}
      </Status.Root>
    )
  }

  if (daysLeft <= 7) {
    return (
      <Status.Root size="sm" colorPalette="green">
        <Status.Indicator />
        Noch {daysLeft} Tage
      </Status.Root>
    )
  }

  return (
    <Status.Root size="sm" colorPalette="blue">
      <Status.Indicator />
      Noch {daysLeft} Tage
    </Status.Root>
  )
}

function capacityBadge(status?: string) {
  const normalized = status?.toLowerCase()

  if (normalized === 'green') {
    return (
      <Badge colorPalette="green" variant="solid">
        Kapazität passt
      </Badge>
    )
  }

  if (normalized === 'yellow') {
    return (
      <Badge colorPalette="yellow" variant="solid">
        Kapazität knapp
      </Badge>
    )
  }

  if (normalized === 'red') {
    return (
      <Badge colorPalette="red" variant="solid">
        Stark überschritten
      </Badge>
    )
  }

  return <Text textAlign="center">—</Text>
}

function getDemandName(demand: MarketplaceDemand) {
  return demand.customerName || demand.bankName || demand.serviceRecipient || '—'
}

function getInterestStatus(demand: MarketplaceDemand) {
  return (
    demand.userInterestStatus === 'INTERESTED' ||
    demand.sibInterestStatus === 'interested' ||
    demand.sibInterestStatus === 'confirmed'
  )
}

export function MarketplaceDemandTable({ demands, onOpenDemand }: MarketplaceDemandTableProps) {
  return (
    <Box overflow="hidden">
      <Table.Root interactive>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Bank</Table.ColumnHeader>
            <Table.ColumnHeader>PLZ</Table.ColumnHeader>
            <Table.ColumnHeader>Ort</Table.ColumnHeader>
            <Table.ColumnHeader>Zeitraum</Table.ColumnHeader>
            <Table.ColumnHeader>Status</Table.ColumnHeader>
            <Table.ColumnHeader>Kapazitäts-Fit</Table.ColumnHeader>
            <Table.ColumnHeader>Interessenten</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {demands.map(demand => {
            const interestedCount = demand.interestsCount ?? 0
            const isOpen = isDemandOpen(demand)

            return (
              <Table.Row key={demand.id} cursor="pointer" onClick={() => onOpenDemand(demand.id)}>
                <Table.Cell>
                  <VStack align="start" gap={1}>
                    <HStack gap={2}>
                      <Text maxW="360px" truncate>
                        {getDemandName(demand)}
                      </Text>

                      {isOpen && <DeadlineStatus expiresAt={demand.expiresAt} />}

                      {getInterestStatus(demand) && (
                        <Badge colorPalette="green" variant="solid">
                          Interesse angemeldet
                        </Badge>
                      )}
                    </HStack>

                    <Text>BLZ</Text>
                  </VStack>
                </Table.Cell>

                <Table.Cell>{demand.postal || '—'}</Table.Cell>

                <Table.Cell>{demand.city || '—'}</Table.Cell>

                <Table.Cell>{formatPeriod(demand.periodStart, demand.periodEnd)}</Table.Cell>

                <Table.Cell>
                  <MarketplaceStatusBadge demand={demand} />
                </Table.Cell>

                <Table.Cell>
                  {isOpen ? capacityBadge(demand.capacityFit) : <Text textAlign="center">—</Text>}
                </Table.Cell>

                <Table.Cell>
                  <Badge colorPalette={isOpen && interestedCount === 0 ? 'red' : 'gray'}>
                    {interestedCount} {interestedCount === 1 ? 'Interessent' : 'Interessenten'}
                  </Badge>
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}
